import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { EventDocument } from 'interfaces/document.interface';
import EventDocumentBody from './eventDocumentBody.component';
import { FileIcon, RootState, UUID } from '@umanforge/common';
import { CrossIcon } from '@common_components/icons/cross.icon';
import Button from '@common_components/buttons/button.component';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { EventService } from 'services/events.service';
import { ToastService } from 'services/toast.service';

interface EventDocumentImageProps {
  file: EventDocument;
  isViewMode?: boolean;
  canDelete: boolean;
  isDeleted: boolean;
  canRotate: boolean;
  deleteFile: ( fileId: UUID ) => void;
  onRotationSaved?: () => void;
  imageVersion?: number;
}

const EventDocumentImage: React.FC<EventDocumentImageProps> = ( { file, isDeleted, canDelete, canRotate, isViewMode, deleteFile, onRotationSaved, imageVersion = 0 } ) => {

  const { t } = useTranslation();
  const [ isOpen, setIsOpen ] = useState( false );
  // Local rotation (delta) applied to the currently displayed file
  const [ rotation, setRotation ] = useState<number>( 0 );
  const [ isSaving, setIsSaving ] = useState( false );

  const dispatch = useDispatch();
  const token = useSelector( ( state: RootState ) => { return state.token.value; } );
  const toastService: ToastService = new ToastService( 'EventDocumentImage' );

  const thumbnailSrc = file.thumbnailUrl && !file.thumbnailUrl.startsWith( 'blob:' )
    ? `${file.thumbnailUrl}?v=${imageVersion}`
    : file.thumbnailUrl;

  const fullImageSrc = file.url && !file.url.startsWith( 'blob:' )
    ? `${file.url}?v=${imageVersion}`
    : file.url;

  const effectiveRotation = ( ( rotation % 360 ) + 360 ) % 360;

  const openPopup = file.thumbnailUrl != null ?
    () => {
      setIsOpen( true );
    }
    :
    undefined;

  const closePopup = () => {
    setIsOpen( false );
    setRotation( 0 );
  };

  useEffect( () => {
    if ( typeof document !== 'undefined' ) {
      document.body.setAttribute( 'data-prev-overflow-img', document.body.style.overflow );
      if ( isOpen ) {
        document.body.style.overflow = 'hidden';

        return () => {
          document.body.style.overflow = document.body.getAttribute( 'data-prev-overflow-img' ) || '';
        };
      } else {
        document.body.style.overflow = document.body.getAttribute( 'data-prev-overflow-img' ) || '';
      }
    }
  }, [ isOpen ] );

  const rotateLeft = () => {
    if ( isDeleted ) {
      return;
    }
    setRotation( prev => { return prev - 90; } );
  };

  const rotateRight = () => {
    if ( isDeleted ) {
      return;
    }
    setRotation( prev => { return prev + 90; } );
  };

  const handleSaveRotation = async () => {
    // Do not allow saving when read-only (no edit rights or already deleted)
    if ( !file.id || !token || isSaving || !canRotate || isDeleted || effectiveRotation === 0 ) {
      return;
    }

    setIsSaving( true );
    try {
      const success = await EventService.updateDocumentRotation( file.id as UUID, rotation, token, dispatch );
      if ( success ) {
        toastService.showSuccess( t( 'rotation_saved' ) );
        // Reset local rotation so we don't apply it again on top of the
        // newly rotated image returned by the backend.
        setRotation( 0 );
        if ( onRotationSaved ) {
          onRotationSaved();
        }
      }
    } catch ( error ) {
      console.error( 'Error saving rotation:', error );
      toastService.showError( t( 'rotation_save_error' ) );
    } finally {
      setIsSaving( false );
    }
  };


  const isRotated = Math.floor( Math.abs( rotation ) / 90 ) % 2 === 1;

  return (
    <>
      <EventDocumentBody
        file={ file }
        canDelete={ canDelete }
        isDeleted={ isDeleted }
        isViewMode={ isViewMode }
        onClickFile={ openPopup }
        deleteFile={ deleteFile }
      >
        {
          file.thumbnailUrl != null ?
            <img
              src={ thumbnailSrc }
              className='object-contain p-1 pointer-events-none'
            />
            :
            <FileIcon
              className={`
                w-full h-full
                flex 
                px-4 py-0.5
                sm:px-5 sm:py-1
                md:px-6 md:py-2
                justify-center 
              `}
            />
        }
      </EventDocumentBody>
      {
        isOpen && ( typeof document !== 'undefined' ? createPortal(
          <div className="fixed inset-0 flex items-center justify-center z-[9999]">
            <div className="absolute inset-0 bg-black/80" onClick={ closePopup } />

            {/* Fixed-position controls so they don't move when image/container resizes */}
            <div className='absolute top-6 right-6 z-50'>
              <Button
                icon={<CrossIcon className="text-white group-hover:rotate-[360deg] transition-transform duration-700" size={7} />}
                color="bg-red-600"
                colorHover="hover:bg-red-700"
                textColor="text-white"
                textColorHover="hover:text-white"
                shadow="shadow-sm"
                onClick={ closePopup }
              />
            </div>

            <div className='absolute top-6 left-6 z-50 flex gap-2'>
              <Button
                icon={<span className='text-white text-xl'>&#10226;</span>}
                tooltip={t( 'RotateLeft' )}
                color='bg-gray-700'
                colorHover='hover:bg-gray-600'
                textColor='text-white'
                textColorHover='hover:text-white'
                shadow='shadow-sm'
                disable={isDeleted}
                onClick={ rotateLeft }
              />
              <Button
                icon={<span className='text-white text-xl'>&#10227;</span>}
                tooltip={t( 'RotateRight' )}
                color='bg-gray-700'
                colorHover='hover:bg-gray-600'
                textColor='text-white'
                textColorHover='hover:text-white'
                shadow='shadow-sm'
                disable={isDeleted}
                onClick={ rotateRight }
              />
              <Button
                label={t( 'save_rotation' )}
                tooltip={t( 'save_rotation' )}
                color='bg-green-600'
                colorHover='hover:bg-green-700'
                textColor='text-white'
                textColorHover='hover:text-white'
                shadow='shadow-sm'
                textSize='text-xs'
                padding='px-4 py-1'
                width='w-[175px]'
                height='h-[50px]'
                onClick={ handleSaveRotation }
                disable={isSaving || !canRotate || isDeleted || effectiveRotation === 0}
              />
            </div>

            <div className='relative flex items-center justify-center w-full h-full p-0'>
              <div className='relative flex items-center justify-center max-w-[92vw] max-h-[92vh] p-2'>
                <img
                  src={ fullImageSrc }
                  style={{
                    transform: `rotate(${rotation}deg)`,
                    transformOrigin: 'center center',
                    maxWidth: isRotated ? '92vh' : '92vw',
                    maxHeight: isRotated ? '92vw' : '92vh',
                    width: 'auto',
                    height: 'auto'
                  }}
                  className='object-contain w-auto h-auto transition-transform duration-300'
                  alt={file.originalName || 'document'}
                />
              </div>
            </div>
          </div>,
          document.body!
        ) : null )
      }
    </>
  );
};

export default EventDocumentImage;
